import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import type { ChartData } from 'chart.js';
import { useTheme } from '../contexts/ThemeContext';
import { useRealTimeData } from '../hooks/useRealTimeData';
import { useSocket } from '../contexts/SocketContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from 'chart.js';
import {
  FiTrendingUp, FiTrendingDown, FiActivity, FiPause, FiPlay,
  FiSettings, FiMaximize2, FiDownload, FiRefreshCw, FiWifi, FiWifiOff
} from 'react-icons/fi';
import { format } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale
);

export interface DataPoint {
  timestamp: Date;
  value: number;
  label?: string;
  metadata?: Record<string, any>;
}

export interface ChartSeries {
  id: string;
  label: string;
  data: DataPoint[];
  color?: string;
  borderColor?: string;
  backgroundColor?: string;
  type?: 'line' | 'bar' | 'area';
  yAxisID?: string;
}

export interface ChartConfig {
  type: 'line' | 'bar' | 'doughnut' | 'area';
  title?: string;
  xAxis?: {
    label?: string;
    type?: 'time' | 'category';
    format?: string;
  };
  yAxis?: {
    label?: string;
    min?: number;
    max?: number;
    format?: string;
  };
  legend?: {
    show: boolean;
    position: 'top' | 'bottom' | 'left' | 'right';
  };
  animation?: boolean;
  responsive?: boolean;
  maintainAspectRatio?: boolean;
}

interface RealTimeChartProps {
  series: ChartSeries[];
  config: ChartConfig;
  isLive?: boolean;
  updateInterval?: number; // in milliseconds
  maxDataPoints?: number;
  onDataUpdate?: () => Promise<ChartSeries[]>;
  showControls?: boolean;
  showStats?: boolean;
  height?: number;
  className?: string;
}

const RealTimeChart: React.FC<RealTimeChartProps> = ({
  series: initialSeries,
  config,
  isLive = false,
  updateInterval = 5000,
  maxDataPoints = 50,
  onDataUpdate,
  showControls = true,
  showStats = true,
  height = 300,
  className = ''
}) => {
  const [series, setSeries] = useState<ChartSeries[]>(initialSeries);
  const [isPlaying, setIsPlaying] = useState(isLive);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);

  const chartRef = useRef<any>(null);
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { colors, themeMode, cssVars } = useTheme();
  const { socket } = useSocket();

  // Real-time data integration
  const realTimeData = useRealTimeData(
    isLive ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws` : undefined,
    {
      autoConnect: isLive,
      heartbeatInterval: 10000,
      maxReconnectAttempts: 5
    }
  );
  
  // Theme-aware color palette for series
  const colorPalette = [
    colors.tokens.chartStroke, '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#F97316', '#06B6D4', '#84CC16', '#EC4899', '#6B7280'
  ];

  // Calculate statistics
  const stats = useMemo(() => {
    const allValues = series.flatMap(s => s.data.map(d => d.value));
    if (allValues.length === 0) return null;

    const latest = series.map(s => ({
      label: s.label,
      value: s.data[s.data.length - 1]?.value || 0,
      change: s.data.length > 1 
        ? s.data[s.data.length - 1]?.value - s.data[s.data.length - 2]?.value 
        : 0,
      color: s.color || colorPalette[0]
    }));

    return {
      total: allValues.reduce((sum, val) => sum + val, 0),
      average: allValues.reduce((sum, val) => sum + val, 0) / allValues.length,
      min: Math.min(...allValues),
      max: Math.max(...allValues),
      latest
    };
  }, [series]);

  // Update data with enhanced error handling and real-time integration
  const updateData = useCallback(async () => {
    if (!onDataUpdate) return;

    setIsLoading(true);
    setError(null);

    try {
      const newSeries = await onDataUpdate();

      // Validate data structure
      if (!Array.isArray(newSeries)) {
        throw new Error('Invalid data format received');
      }

      // Limit data points to prevent performance issues
      const limitedSeries = newSeries.map(s => {
        if (!s.data || !Array.isArray(s.data)) {
          return { ...s, data: [] };
        }
        return {
          ...s,
          data: s.data.slice(-maxDataPoints)
        };
      });

      setSeries(limitedSeries);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to update chart data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update data';
      setError(errorMessage);

      // Auto-retry logic for network errors
      if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setTimeout(() => updateData(), 5000);
      }
    } finally {
      setIsLoading(false);
    }
  }, [onDataUpdate, maxDataPoints]);

  // Start/stop auto-update
  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  // Manual refresh
  const handleRefresh = () => {
    updateData();
  };

  // Real-time data subscription effect
  useEffect(() => {
    if (isLive && realTimeData.isConnected && socket) {
      const handleRealTimeUpdate = (data: any) => {
        if (data && data.series) {
          setSeries(prevSeries => {
            return data.series.map((newSeries: ChartSeries) => {
              const existingSeries = prevSeries.find(s => s.id === newSeries.id);
              if (existingSeries) {
                // Merge with existing data
                const mergedData = [...existingSeries.data, ...newSeries.data].slice(-maxDataPoints);
                return {
                  ...existingSeries,
                  data: mergedData
                };
              }
              return newSeries;
            });
          });
          setLastUpdate(new Date());
          setError(null);
        }
      };

      // Subscribe to real-time analytics updates
      socket.on('analytics.update', handleRealTimeUpdate);
      socket.on('messages.activity', handleRealTimeUpdate);
      socket.on('performance.metrics', handleRealTimeUpdate);

      return () => {
        socket.off('analytics.update', handleRealTimeUpdate);
        socket.off('messages.activity', handleRealTimeUpdate);
        socket.off('performance.metrics', handleRealTimeUpdate);
      };
    }
  }, [isLive, realTimeData.isConnected, socket, maxDataPoints]);

  // Setup auto-update timer (fallback when real-time is not available)
  useEffect(() => {
    if (isPlaying && onDataUpdate && (!isLive || !realTimeData.isConnected)) {
      updateTimerRef.current = setInterval(updateData, updateInterval);
    } else if (updateTimerRef.current) {
      clearInterval(updateTimerRef.current);
      updateTimerRef.current = null;
    }

    return () => {
      if (updateTimerRef.current) {
        clearInterval(updateTimerRef.current);
      }
    };
  }, [isPlaying, updateInterval, onDataUpdate, isLive, realTimeData.isConnected, updateData]);

  // Handle connection status changes
  useEffect(() => {
    if (realTimeData.error) {
      setError(`Real-time connection error: ${realTimeData.error}`);
    } else if (realTimeData.isConnected) {
      setError(null);
    }
  }, [realTimeData.error, realTimeData.isConnected]);

  // Prepare chart data based on type
  const chartData: ChartData<'line' | 'bar' | 'doughnut', number[], string> = useMemo(() => {
    if (config.type === 'doughnut') {
      // For doughnut charts, use latest values
      const labels = series.map(s => s.label);
      const datasets = [{
        label: config.title || 'Data',
        data: series.map(s => s.data[s.data.length - 1]?.value || 0),
        backgroundColor: series.map((s, i) => s.backgroundColor || s.color || colorPalette[i]),
        borderColor: series.map((s, i) => s.borderColor || s.color || colorPalette[i]),
        borderWidth: 2,
      }];

      return { labels, datasets };
    }

    // For time-series charts
    const labels = series[0]?.data.map(point =>
      // Use formatted string labels to satisfy ChartData<string>
      format(point.timestamp, 'MMM d HH:mm')
    ) || [];
    const datasets = series.map((s, index) => {
      const baseColor = s.color || colorPalette[index % colorPalette.length];

      return {
        label: s.label,
        data: s.data.map(point => point.value),
        borderColor: s.borderColor || baseColor,
        backgroundColor: s.backgroundColor || baseColor,
        fill: config.type === 'area' || s.type === 'area',
        tension: config.type === 'line' ? 0.4 : 0,
        pointRadius: config.type === 'line' ? 3 : 0,
        pointHoverRadius: 6,
        borderWidth: 2,
      };
    });

    return { labels, datasets };
  }, [series, config.type]);

  // Chart options
  const chartOptions = useMemo(() => {
    const baseOptions: any = {
      responsive: config.responsive !== false,
      maintainAspectRatio: config.maintainAspectRatio !== false,
      animation: config.animation === false ? false : { duration: 750, easing: 'easeInOutQuart' },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      plugins: {
        legend: {
          display: config.legend?.show !== false,
          position: config.legend?.position || 'top',
        },
        tooltip: {
          backgroundColor: cssVars['--chart-tooltip-bg'],
          titleColor: colors.tokens.textPrimary,
          bodyColor: colors.tokens.textPrimary,
          borderColor: cssVars['--chart-tooltip-border'],
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            label: (context: any) => {
              const value = context.parsed.y;
              const formatted = typeof value === 'number' 
                ? value.toLocaleString() 
                : value;
              return `${context.dataset.label}: ${formatted}`;
            },
            title: (context: any) => {
              if (config.xAxis?.type === 'time' && context[0]?.parsed?.x) {
                return format(new Date(context[0].parsed.x), 'MMM d, yyyy HH:mm:ss');
              }
              return context[0]?.label || '';
            }
          }
        }
      },
      scales: config.type !== 'doughnut' ? {
        x: {
          type: config.xAxis?.type === 'time' ? 'category' : 'category',
          display: true,
          grid: { 
            color: cssVars['--chart-grid'],
          },
          ticks: { 
            color: cssVars['--chart-axis-labels'],
            font: {
              family: 'DM Sans, Inter, sans-serif',
              size: 14,
              weight: '400'
            }
          },
          title: {
            display: !!config.xAxis?.label,
            text: config.xAxis?.label,
            color: colors.tokens.textSecondary,
            font: {
              family: 'DM Sans, Inter, sans-serif',
              size: 12,
              weight: '500'
            }
          }
        },
        y: {
          display: true,
          grid: { 
            color: cssVars['--chart-grid'],
          },
          ticks: { 
            color: cssVars['--chart-axis-labels'],
            font: {
              family: 'DM Sans, Inter, sans-serif',
              size: 14,
              weight: '400'
            }
          },
          title: {
            display: !!config.yAxis?.label,
            text: config.yAxis?.label,
            color: colors.tokens.textSecondary,
            font: {
              family: 'DM Sans, Inter, sans-serif',
              size: 12,
              weight: '500'
            }
          },
          min: config.yAxis?.min,
          max: config.yAxis?.max,
          beginAtZero: config.yAxis?.min === undefined,
        }
      } : undefined,
    };

    return baseOptions;
  }, [config]);

  // Get chart component based on type
  const getChartComponent = () => {
    switch (config.type) {
      case 'line':
      case 'area': {
        const data = chartData as ChartData<'line', number[], string>;
        return <Line ref={chartRef} data={data} options={chartOptions} height={height} />;
      }
      case 'bar': {
        const data = chartData as ChartData<'bar', number[], string>;
        return <Bar ref={chartRef} data={data} options={chartOptions} height={height} />;
      }
      case 'doughnut': {
        const data = chartData as ChartData<'doughnut', number[], string>;
        return <Doughnut ref={chartRef} data={data} options={chartOptions} height={height} />;
      }
      default: {
        const data = chartData as ChartData<'line', number[], string>;
        return <Line ref={chartRef} data={data} options={chartOptions} height={height} />;
      }
    }
  };

  return (
    <div 
      className={`relative rounded-lg ${className}`} 
      data-theme={themeMode}
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-primary)'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {config.title && (
            <h3 className="text-lg font-semibold text-gray-900">
              {config.title}
            </h3>
          )}
          
          <div className="flex items-center gap-3 text-sm">
            {/* Connection Status Indicator */}
            <div className="flex items-center gap-1">
              {isLive && realTimeData.isConnected ? (
                <FiWifi size={14} className="text-green-500" />
              ) : isLive && realTimeData.isLoading ? (
                <div className="w-2 h-2 border border-blue-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FiWifiOff size={14} className="text-red-500" />
              )}
              <span className={`${
                isLive && realTimeData.isConnected ? 'text-green-500' :
                isLive && realTimeData.isLoading ? 'text-blue-500' :
                'text-gray-500'
              }`}>
                {isLive && realTimeData.isConnected ? 'Real-time' :
                 isLive && realTimeData.isLoading ? 'Connecting...' :
                 isPlaying ? 'Live (HTTP)' : 'Paused'}
              </span>
            </div>

            {/* Last Update Time */}
            {lastUpdate && (
              <span className="text-gray-500">
                • Updated {format(lastUpdate, 'HH:mm:ss')}
              </span>
            )}

            {/* Connection Details */}
            {isLive && realTimeData.reconnectCount > 0 && (
              <span className="text-orange-500">
                • Reconnected {realTimeData.reconnectCount}x
              </span>
            )}
          </div>
        </div>

        {/* Controls */}
        {showControls && (
          <div className="flex items-center gap-2">
            {onDataUpdate && (
              <>
                <button
                  onClick={togglePlayback}
                  className={`p-2 rounded-lg transition-colors ${
                    isPlaying 
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                  title={isPlaying ? 'Pause updates' : 'Resume updates'}
                >
                  {isPlaying ? <FiPause size={16} /> : <FiPlay size={16} />}
                </button>
                
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  title="Refresh now"
                >
                  <FiRefreshCw 
                    size={16} 
                    className={isLoading ? 'animate-spin' : ''} 
                  />
                </button>
              </>
            )}

            {/* Real-time connection controls */}
            {isLive && (
              <>
                <button
                  onClick={() => realTimeData.connect()}
                  disabled={realTimeData.isConnected || realTimeData.isLoading}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  title="Reconnect real-time data"
                >
                  <FiWifi size={16} />
                </button>

                <button
                  onClick={() => realTimeData.disconnect()}
                  disabled={!realTimeData.isConnected}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  title="Disconnect real-time data"
                >
                  <FiWifiOff size={16} />
                </button>
              </>
            )}

            <button
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              title="Chart settings"
            >
              <FiSettings size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      {showStats && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {stats.latest.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 truncate">
                    {stat.label}
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {stat.value.toLocaleString()}
                  </p>
                </div>
                <div className={`flex items-center text-xs ${
                  stat.change > 0 ? 'text-green-600' : 
                  stat.change < 0 ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {stat.change > 0 ? <FiTrendingUp size={14} /> : 
                   stat.change < 0 ? <FiTrendingDown size={14} /> : null}
                  <span className="ml-1">
                    {stat.change > 0 ? '+' : ''}
                    {stat.change.toLocaleString()}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Chart */}
      <div className="relative" style={{ height: height }}>
        {series.length > 0 ? (
          getChartComponent()
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <FiActivity size={48} className="mx-auto mb-2 opacity-50" />
              <p>No data available</p>
            </div>
          </div>
        )}
        
        {/* Loading overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-white/80 flex items-center justify-center"
            >
              <div className="flex items-center gap-2 text-gray-600">
                <FiRefreshCw className="animate-spin" size={16} />
                <span className="text-sm">Updating...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RealTimeChart;